import { useState, useCallback } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Card,
  Image,
  ActionIcon,
  ScrollArea,
  Grid,
  FileButton,
  Divider,
  Badge,
  Alert,
} from '@mantine/core';
import { IconTrash, IconUpload, IconPhoto, IconInfoCircle } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  loadImageHistory,
  addImageToHistory,
  removeImageFromHistory,
  getImageHistoryStats,
  type StoredImage,
} from '../../utils/storage/imageHistory';

interface ImageHistoryModalProps {
  opened: boolean;
  onClose: () => void;
  onImageSelect: (imageData: string, imageName: string) => void;
}

export function ImageHistoryModal({ opened, onClose, onImageSelect }: ImageHistoryModalProps) {
  const [images, setImages] = useState<StoredImage[]>(() => loadImageHistory().images);
  const [isUploading, setIsUploading] = useState(false);

  const refreshImages = useCallback(() => {
    setImages(loadImageHistory().images);
  }, []);

  const handleNewImageUpload = useCallback((file: File | null) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      notifications.show({
        title: 'Erreur',
        message: 'Veuillez sélectionner un fichier image valide (PNG, JPEG, etc.)',
        color: 'red',
      });
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        const savedImage = addImageToHistory(file.name, result);
        if (savedImage) {
          refreshImages();
          onImageSelect(result, file.name);
          onClose();
          notifications.show({
            title: 'Image ajoutée',
            message: 'L\'image a été ajoutée à votre historique et sélectionnée',
            color: 'green',
          });
        } else {
          notifications.show({
            title: 'Erreur',
            message: 'Impossible d\'ajouter l\'image à l\'historique (trop volumineux?)',
            color: 'red',
          });
        }
      }
      setIsUploading(false);
    };
    reader.onerror = () => {
      setIsUploading(false);
      notifications.show({
        title: 'Erreur',
        message: 'Erreur lors de la lecture du fichier',
        color: 'red',
      });
    };
    reader.readAsDataURL(file);
  }, [onImageSelect, onClose, refreshImages]);

  const handleImageSelect = useCallback((image: StoredImage) => {
    onImageSelect(image.data, image.name);
    onClose();
  }, [onImageSelect, onClose]);

  const handleImageDelete = useCallback((imageId: string, imageName: string) => {
    if (removeImageFromHistory(imageId)) {
      refreshImages();
      notifications.show({
        title: 'Image supprimée',
        message: `L'image "${imageName}" a été supprimée de l'historique`,
        color: 'blue',
      });
    } else {
      notifications.show({
        title: 'Erreur',
        message: 'Impossible de supprimer l\'image',
        color: 'red',
      });
    }
  }, [refreshImages]);

  const stats = getImageHistoryStats();
  const formattedSize = (stats.totalSize / 1024).toFixed(1);
  const maxSize = (stats.maxSize / 1024).toFixed(0);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Sélectionner une image"
      size="lg"
      centered
    >
      <Stack gap="md">
        {/* Upload new image section */}
        <Card withBorder p="md">
          <Stack gap="sm">
            <Group gap="sm">
              <IconUpload size={20} />
              <Text fw={500}>Ajouter une nouvelle image</Text>
            </Group>
            <FileButton accept="image/*" onChange={handleNewImageUpload}>
              {(props) => (
                <Button
                  {...props}
                  variant="light"
                  leftSection={<IconPhoto size={16} />}
                  loading={isUploading}
                  fullWidth
                >
                  Télécharger une image
                </Button>
              )}
            </FileButton>
          </Stack>
        </Card>

        <Divider label="Ou choisir dans l'historique" labelPosition="center" />

        {/* Storage info */}
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          <Text size="sm">
            {stats.count} image{stats.count !== 1 ? 's' : ''} stockée{stats.count !== 1 ? 's' : ''} 
            ({formattedSize} Ko / {maxSize} Ko utilisés)
          </Text>
        </Alert>

        {/* Image gallery */}
        {images.length > 0 ? (
          <ScrollArea h={300}>
            <Grid>
              {images
                .sort((a, b) => b.timestamp - a.timestamp) // Most recent first
                .map((image) => (
                  <Grid.Col span={6} key={image.id}>
                    <Card
                      withBorder
                      p="xs"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleImageSelect(image)}
                    >
                      <Stack gap="xs">
                        <Image
                          src={image.data}
                          alt={image.name}
                          height={80}
                          fit="contain"
                          fallbackSrc="data:image/svg+xml,%3csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%3e%3cpath%20fill='%23ccc'%20d='M19%207h-3V6a2%202%200%200%200-2-2H8a2%202%200%200%200-2%202v1H3a1%201%200%200%200-1%201v11a3%203%200%200%200%203%203h14a3%203%200%200%200%203-3V8a1%201%200%200%200-1-1zM8%206h8v1H8V6zm10%2013H6a1%201%200%200%201-1-1V9h2v1a1%201%200%200%200%202%200V9h6v1a1%201%200%200%200%202%200V9h2v9a1%201%200%200%201-1%201z'/%3e%3c/svg%3e"
                        />
                        <Group justify="space-between" align="center">
                          <Text size="xs" truncate flex={1}>
                            {image.name}
                          </Text>
                          <ActionIcon
                            size="sm"
                            color="red"
                            variant="subtle"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageDelete(image.id, image.name);
                            }}
                          >
                            <IconTrash size={14} />
                          </ActionIcon>
                        </Group>
                        <Badge size="xs" variant="light">
                          {new Date(image.timestamp).toLocaleDateString('fr-FR')}
                        </Badge>
                      </Stack>
                    </Card>
                  </Grid.Col>
                ))}
            </Grid>
          </ScrollArea>
        ) : (
          <Card withBorder p="xl">
            <Stack align="center" gap="sm">
              <IconPhoto size={48} color="gray" />
              <Text size="sm" c="dimmed" ta="center">
                Aucune image dans l'historique.
                <br />
                Téléchargez votre première image ci-dessus.
              </Text>
            </Stack>
          </Card>
        )}

        {/* Action buttons */}
        <Group justify="end">
          <Button variant="subtle" onClick={onClose}>
            Annuler
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}